import { z } from 'zod';
import { sentencesOf } from './coach-voice.js';
import { idSchema, paginated, slugSchema } from './common.js';
/**
 * A lesson's new terms, and the ones a learner keeps — LESSON-18.
 *
 * Stored per lesson rather than in a shared glossary, so a saved term is (learner,
 * lesson, term). Nothing here imports `content.js`, which imports this.
 */
const MAX_TERMS = 12;
const MAX_DEFINITION = 300;
const termTextSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .describe('The term as the lesson writes it.');
export const definitionSchema = z
    .string()
    .trim()
    .min(1)
    .max(MAX_DEFINITION)
    .refine((text) => sentencesOf(text).length <= 2, {
    message: 'A definition is one or two sentences.',
})
    .describe('What the term means, in one or two sentences.');
export const lessonTermInputSchema = z
    .strictObject({
    term: termTextSchema,
    definition: definitionSchema,
    taughtInLessonId: idSchema
        .optional()
        .describe('The earlier lesson that taught this term. Must be a real lesson other than this one.'),
})
    .describe('One term as an editor writes it.');
/** A list of terms with no term twice, whatever shape one term takes. */
const termList = (term) => z
    .array(term)
    .max(MAX_TERMS)
    .superRefine((terms, ctx) => {
    const seen = new Set();
    terms.forEach(({ term }, index) => {
        const key = term.toLowerCase();
        if (seen.has(key))
            ctx.addIssue({
                code: 'custom',
                path: [index, 'term'],
                message: `"${term}" is listed twice.`,
            });
        seen.add(key);
    });
});
export const lessonTermsInputSchema = termList(lessonTermInputSchema).describe('Every term the lesson defines, in order. Replaces the list; a term kept by text keeps its saves.');
/**
 * The list a generator writes — GEN-124. No `taughtInLessonId`: a model has never seen a
 * lesson id, and an invented one refuses the whole lesson write.
 */
export const plannedTermsSchema = termList(lessonTermInputSchema.omit({ taughtInLessonId: true })).describe('Every new term or abbreviation the lesson uses, in the order a learner meets it, each defined in plain words.');
const termLessonSchema = z
    .object({
    id: idSchema,
    slug: slugSchema,
    title: z.string().min(1),
})
    .describe('A lesson a term links to.');
export const lessonTermSchema = z
    .object({
    term: termTextSchema,
    definition: z.string().min(1),
    taughtIn: termLessonSchema
        .optional()
        .describe('The earlier lesson that taught it. The learner read omits it while that lesson is not published, so a link always opens.'),
})
    .describe('A term this lesson uses.');
export const savedTermSchema = lessonTermSchema
    .extend({
    lesson: termLessonSchema.describe('The lesson the term was saved from.'),
    savedAt: z.iso.datetime(),
})
    .describe('One term a learner kept.');
export const lessonSavedTermsSchema = z
    .object({
    terms: z
        .array(termTextSchema)
        .max(MAX_TERMS)
        .describe('The terms of this lesson the caller has saved.'),
})
    .describe('GET /lessons/:slug/saved-terms');
export const savedTermsSchema = paginated(savedTermSchema).describe('GET /me/saved-terms');
//# sourceMappingURL=terms.js.map